import { useHasApiKey } from "@crm/ai/user-ai-credentials";
import { Button } from "@crm/ui/components/ui/button";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Option } from "effect";
import * as AsyncResult from "effect/unstable/reactivity/AsyncResult";
import { ArrowRight, Info, Plus } from "lucide-react";
import { useSioHomeSummary } from "#app/sio/sio-home.queries";
import { useActiveSite, useToolGap } from "#app/sites";
import { ToolGapCard } from "#components/tool-gap-card";

export const Route = createFileRoute("/_protected/sio-tools/_layout/")({
  head: () => ({}),
  component: SioToolsHomePage,
});

/** 漏斗数字（typographic，无盒子） */
function FunnelStage({
  num,
  name,
  hot,
  to,
}: {
  num: number;
  name: string;
  hot?: boolean;
  to: string;
}) {
  return (
    <Link className="group flex-1" to={to}>
      <div
        className={`font-bold font-display text-[30px] leading-none tracking-tight ${
          hot ? "text-violet" : "text-ink"
        }`}
      >
        {num}
      </div>
      <div className="mt-2 flex items-center gap-1.5 font-semibold text-[12px] text-ink-2 group-hover:text-ink">
        <span className={`h-[5px] w-[5px] rounded-full ${hot ? "bg-violet" : "bg-border"}`} />
        {name}
      </div>
    </Link>
  );
}

function Metric({ num, name }: { num: number; name: string }) {
  return (
    <div className="flex-1">
      <div className="font-bold font-display text-[30px] text-ink leading-none tracking-tight">
        {num}
      </div>
      <div className="mt-2 font-semibold text-[12px] text-ink-2">{name}</div>
    </div>
  );
}

function SioToolsHomePage() {
  const hasApiKey = useHasApiKey();
  const toolGap = useToolGap(hasApiKey);
  const activeSite = useActiveSite();
  const summaryResult = useSioHomeSummary();
  const summary = AsyncResult.value(summaryResult).pipe(Option.getOrNull);
  const topics = summary?.topics ?? { pool: 0, co: 0, write: 0, done: 0 };
  const ads = summary?.ads ?? { groups: 0, rsa: 0, keywords: 0 };

  return (
    <div className="container mx-auto max-w-6xl px-4 py-8">
      {/* Hero */}
      <div className="mb-7">
        <span className="mb-3.5 inline-flex items-center rounded-full bg-brand-soft px-3 py-1 font-semibold text-[12px] text-brand-text uppercase tracking-widest">
          推广工具
        </span>
        <h1 className="mb-2 font-display font-semibold text-[34px] text-ink leading-tight tracking-tight">
          以<span className="font-display text-brand italic">搜索意图</span>
          为核心，<span className="font-display text-brand italic">两条线</span>
          一起跑
        </h1>
        <p className="max-w-xl text-[14.5px] text-ink-2 leading-relaxed">
          内容线沉淀选题与文章拿自然流量，投放线围绕着陆页打付费流量
          {activeSite ? `——当前服务站点：${activeSite.name}` : ""}。
        </p>
      </div>

      {toolGap.status === "ready" && toolGap.gap ? (
        <ToolGapCard
          gap={toolGap.gap}
          settingsTo="/sio-tools/settings"
          siteId={activeSite?.id}
          siteName={activeSite?.name}
        />
      ) : null}

      {/* 内容线 */}
      <div className="mb-5 rounded-2xl border border-border bg-card p-6 shadow-s">
        <div className="mb-6 flex items-baseline justify-between">
          <div className="flex items-center gap-2.5">
            <h3 className="font-bold text-[17px] text-ink">内容线</h3>
            <span className="font-extrabold text-[10px] text-ink-3 tracking-widest">ORGANIC</span>
          </div>
          <Link
            className="font-semibold text-[12.5px] text-brand hover:text-brand-deep"
            to="/sio-tools/pipeline"
          >
            进入流水线 →
          </Link>
        </div>

        <div className="flex items-start px-1">
          <FunnelStage name="选题库" num={topics.pool} to="/sio-tools/topics" />
          <FunnelStage hot name="共创中" num={topics.co} to="/sio-tools/pipeline" />
          <FunnelStage name="待写作" num={topics.write} to="/sio-tools/pipeline" />
          <FunnelStage name="已完成" num={topics.done} to="/sio-tools/pipeline" />
        </div>

        <div className="mt-7 flex items-center gap-5 border-border border-t pt-5">
          <Link to="/sio-tools/topics">
            <Button size="sm">
              <Plus size={14} />
              添加话题
            </Button>
          </Link>
          <Link
            className="ml-auto font-semibold text-[12.5px] text-ink-2 hover:text-ink"
            to="/sio-tools/topics"
          >
            选题库（{topics.pool}） →
          </Link>
        </div>
      </div>

      {/* 投放线 */}
      <div className="mb-7 rounded-2xl border border-border bg-card p-6 shadow-s">
        <div className="mb-6 flex items-baseline justify-between">
          <div className="flex items-center gap-2.5">
            <h3 className="font-bold text-[17px] text-ink">投放线</h3>
            <span className="font-extrabold text-[10px] text-ink-3 tracking-widest">PAID</span>
          </div>
          <Link
            className="font-semibold text-[12.5px] text-brand hover:text-brand-deep"
            to="/sio-tools/ads"
          >
            全部广告组 →
          </Link>
        </div>

        <div className="flex px-1">
          <Metric num={ads.groups} name="广告组" />
          <div className="border-border border-l pl-6">
            <Metric num={ads.rsa} name="RSA 文案" />
          </div>
          <div className="border-border border-l pl-6">
            <Metric num={ads.keywords} name="关键词" />
          </div>
        </div>

        <div className="mt-7 flex items-center gap-5 border-border border-t pt-5">
          <Link to="/sio-tools/keywords">
            <Button size="sm">
              关键词研究
              <ArrowRight size={14} />
            </Button>
          </Link>
          <Link
            className="ml-auto font-semibold text-[12.5px] text-ink-2 hover:text-ink"
            to="/sio-tools/ads"
          >
            ＋ 新建广告组
          </Link>
        </div>
      </div>

      {/* 路径提示 */}
      <div className="mb-7 flex items-center gap-2 pl-1 text-[12px] text-ink-3">
        <Info size={13} />
        内容线：选题库 → 共创 Brief → 写作 → 发布；投放线：关键词研究 → 着陆页广告组 → RSA 文案 →
        投放
      </div>
    </div>
  );
}
